import React, { useEffect, useState } from 'react';
import { CircularProgress, Typography, Button, Box, Slider } from '@mui/material';
import DocumentService, { downloadInBrowser } from 'services/document';
import { S3Object } from 'API';
import { getCacheWithExpiration, imgUrlToFile } from 'shared/utilities';
import { DICTIONARY } from 'shared/constants';
import usePhotoEditor from 'shared/hooks/usePhotoEditor';

interface Props {
  className?: string;
  s3Object: S3Object;
}

const ImageViewer: React.FC<Props> = ({ className, s3Object }: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageData, setImageData] = useState<DocumentAPIResponse>();
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    let ignore = false;
    const getImageFile = async () => {
      try {
        const image = await getCacheWithExpiration(
          JSON.stringify(s3Object),
          async () => DocumentService.getDocument(s3Object),
          14 * 60 * 1000, // 14 minutes
        );

        if (!ignore) {
          setImageData(image);
          const imgFile = await imgUrlToFile(image);
          setImageFile(imgFile);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    getImageFile();

    return () => { ignore = true; };
  }, []);

  const {
    canvasRef,
    brightness,
    setBrightness,
    contrast,
    setContrast,
    rotate,
    setRotate,
    zoom,
    setZoom,
    downloadImage,
    resetFilters,
  } = usePhotoEditor({ file: imageFile as File });

  return (
    <Box
      component="figure"
      className={className}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
    >

      {isLoading && (
        <CircularProgress />
      )}

      {!isLoading && !imageFile
        && <Typography>{DICTIONARY.DownloadError}</Typography>}

      {!isLoading && imageFile && (
        <>
          <Box
            sx={(t) => ({
              borderBottom: `1px solid ${t.palette.divider}`,
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0.25rem',
              width: '100%',
              '& > *': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
            })}
          >

            <Box>
              <Box sx={{ flexDirection: 'column', padding: '0.75rem' }}>
                <Box sx={{ width: '12rem' }}>
                  <Typography>Brightness</Typography>
                  <Slider min={0} max={200} value={brightness} onChange={(_, newValue) => setBrightness(Number(newValue))} />
                </Box>
                <Box sx={{ width: '12rem' }}>
                  <Typography>Contrast</Typography>
                  <Slider min={0} max={200} value={contrast} onChange={(_, newValue) => setContrast(Number(newValue))} />
                </Box>
              </Box>

              <Box sx={{ flexDirection: 'column', padding: '0.75rem' }}>
                <Box sx={{ width: '12rem' }}>
                  <Typography>Rotate</Typography>
                  <Slider min={0} max={360} value={rotate} onChange={(_, newValue) => setRotate(Number(newValue))} />
                </Box>
                <Box sx={{ width: '12rem' }}>
                  <Typography>Zoom</Typography>
                  <Slider min={0} max={3} step={0.1} value={zoom} onChange={(_, newValue) => setZoom(Number(newValue))} />
                </Box>
              </Box>
            </Box>

            <Box sx={{ padding: '0.75rem' }}>
              <Button
                sx={{ marginLeft: '0.5rem' }}
                data-function="save"
                color="primary"
                onClick={resetFilters}
              >
                Reset
              </Button>
              <Button
                sx={{ marginLeft: '0.5rem' }}
                data-function="download"
                color="secondary"
                onClick={() => {
                  if (imageData) {
                    downloadInBrowser(imageData.signedUrl, imageData.fileName);
                  }
                }}
                disabled={!imageData}
              >
                Download
              </Button>
              <Button
                sx={{ marginLeft: '0.5rem' }}
                color="secondary"
                data-function="cancel"
                onClick={downloadImage}
              >
                Save
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', position: 'relative' }}>
            <canvas
              ref={canvasRef}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100% - 100px)',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                margin: 'auto',
                cursor: 'pointer',
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ImageViewer;
